import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient } from '@/contexts/QueryProvider'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { formatUsd } from './formatUsd'
import { restErrorMessage } from './restErrorMessage'
import { useCurrentOrganisation } from '../useCurrentOrganisation'

const formSchema = z.object({
  limitUsd: z
    .string()
    .refine(
      (value) => value.trim() === '' || (!Number.isNaN(Number(value)) && Number(value) >= 0),
      'Must be a non-negative number'
    ),
})

type TDefaultTokenLimitForm = z.infer<typeof formSchema>

function toFieldValue(defaultLimitUsd: number | null | undefined): string {
  return defaultLimitUsd == null ? '' : String(defaultLimitUsd)
}

export function DefaultTokenLimitSection({
  defaultLimitUsd,
  canEdit,
}: {
  defaultLimitUsd: number | null | undefined
  canEdit: boolean
}) {
  const hasDefault = defaultLimitUsd != null

  return (
    <SectionContainer
      title="Default"
      description="Daily token spend cap in USD applied to every organisation user who does not have an override."
    >
      {canEdit ? (
        <DefaultTokenLimitForm defaultLimitUsd={defaultLimitUsd} hasDefault={hasDefault} />
      ) : (
        <p className="text-base">
          {hasDefault ? `${formatUsd(defaultLimitUsd)} per day` : 'No limit'}
        </p>
      )}
    </SectionContainer>
  )
}

function DefaultTokenLimitForm({
  defaultLimitUsd,
  hasDefault,
}: {
  defaultLimitUsd: number | null | undefined
  hasDefault: boolean
}) {
  const { id: orgId } = useCurrentOrganisation()
  const { getSubscriptionsApiGetOrganisationTokenLimits, postSubscriptionsApiSetOrganisationDefaultTokenLimit } =
    useRestClient()

  const form = useForm<TDefaultTokenLimitForm>({
    resolver: zodResolver(formSchema),
    defaultValues: { limitUsd: toFieldValue(defaultLimitUsd) },
  })

  useEffect(() => {
    form.reset({ limitUsd: toFieldValue(defaultLimitUsd) })
  }, [defaultLimitUsd, form])

  const invalidate = () =>
    getSubscriptionsApiGetOrganisationTokenLimits.invalidateQueries({
      parameters: { path: { orgId } },
    })

  const { mutate: setDefault, isPending } = postSubscriptionsApiSetOrganisationDefaultTokenLimit.useMutation(
    undefined,
    {
      onSuccess: (_data, variables) => {
        invalidate()
        const cleared = variables.body?.limitUsd == null
        toast.success(cleared ? 'Organisation default token limit cleared' : 'Organisation default token limit saved')
      },
      onError: (error) => toast.error(restErrorMessage(error, 'Failed to save organisation default token limit')),
    }
  )

  const saveLimit = (limitUsd: number | null) => {
    setDefault({
      path: { orgId },
      body: { limitUsd },
    })
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => {
          const trimmed = data.limitUsd.trim()
          saveLimit(trimmed === '' ? null : Number(trimmed))
        })}
        className="flex max-w-xl flex-col gap-3"
      >
        <FormField
          control={form.control}
          name="limitUsd"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Daily limit (USD)</FormLabel>
              <FormControl>
                <Input type="number" min={0} step="0.01" placeholder="No limit" {...field} />
              </FormControl>
              <FormDescription>Leave empty and save to remove the organisation default. Zero is a cap, not unlimited.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-3">
          <Button type="submit" disabled={isPending}>
            Save
          </Button>
          {hasDefault && (
            <ConfirmDialog
              title="Clear organisation default"
              description="Remove the organisation daily cap? Users without an override will have no daily token limit."
              confirmLabel="Clear"
              confirmVariant="destructive"
              onConfirm={() => saveLimit(null)}
              trigger={
                <Button type="button" variant="outline" disabled={isPending}>
                  Clear
                </Button>
              }
            />
          )}
        </div>
      </form>
    </Form>
  )
}
